import {OrderRequest} from '../../declarations/types'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {isEmpty, isEqual, uniq} from 'lodash'
import {trackEvent} from '../../../common/analytics'
import {LoadType} from '../../../App.constants'
import {isTwelveHoursFormat} from '../../components/TimeScroller/TimeScroller.utils'

interface TrackSubmissionParams {
  quantityType: string
  updatedDeliveries: OrderRequest[]
  data: BulkOrderFormValues
  defaultBusinessLine: string
  orderIntakeCustomerReferenceField: boolean
  orders: OrderRequest[]
  orderSessionEventId: string
  language: string
  loadType: LoadType
  shippingType: ShippingType
  trackAlternativePhone: boolean
}

export const trackSubmissionAnalytics = ({
  quantityType,
  updatedDeliveries,
  data,
  defaultBusinessLine,
  orderIntakeCustomerReferenceField,
  orders,
  orderSessionEventId,
  language,
  loadType,
  shippingType,
  trackAlternativePhone
}: TrackSubmissionParams) => {
  if (!isEmpty(data.driverInstructions)) {
    trackEvent('hubOrderIntakeInstructionAdded')
    const isCustomerInstructionsAddedRegex = new RegExp(/(\w|\s|\W)+Customer: (\w|\s|\W)+/g)
    if (isCustomerInstructionsAddedRegex.test(data.driverInstructions ?? '')) {
      trackEvent('hubOrderIntakeAdditionalDriverInstructionsAdded')
    }
  }

  updatedDeliveries.forEach((delivery) => {
    if (delivery.payload.deliveryDate !== delivery.initialDeliveryDate) {
      trackEvent('hubOrderIntakeDefaultDateNotUsed')
    }
    if (!isEqual(delivery.payload.deliveryTime, delivery.initialDeliveryTime)) {
      trackEvent('hubOrderIntakeDefaultTimeNotUsed')
    }
    if (delivery.payload.capacity.quantity !== delivery.initialTruckQuantity) {
      trackEvent('hubOrderIntakeDefaultQuantityNotUsed')
    }
    if (!isEqual(delivery.payload.contact?.fullName, delivery.initialFullName)) {
      trackEvent('hubOrderIntakeOrderPlacerNameChanged')
    }
  })

  const orderedMaterials = uniq(
    updatedDeliveries.map((delivery) => delivery.payload.materialEnteredNumber)
  )

  trackEvent('hubOrderIntakeEnd', {
    shippingType: shippingType,
    numberOfMaterials: orderedMaterials.length,
    businessLine: defaultBusinessLine,
    quantityType,
    orderVolume: updatedDeliveries.reduce((result, delivery) => {
      return {
        ...result,
        [delivery.payload.materialEnteredNumber]:
          (result[delivery.payload.materialEnteredNumber] || 0) + delivery.payload.capacity.quantity
      }
    }, {}),
    numberOfOrderRequests: updatedDeliveries.length,
    purchaseOrderNumberVisible: orderIntakeCustomerReferenceField,
    type: loadType,
    materials: orderedMaterials
  })

  if (trackAlternativePhone) {
    const contact = updatedDeliveries[0].payload.contact

    if (contact?.mainPhone !== contact?.backupPhone)
      trackEvent('hubOrderIntakeAlternativePhoneNumberUsed')
  }

  const isTwelveHours = isTwelveHoursFormat(language)

  orders.map((delivery) => {
    if (delivery.timeTrackingData) {
      delivery.timeTrackingData.forEach((userInteraction) => {
        trackEvent('hubOrderIntakeEditTime', {
          orderSessionEventId,
          cardId: userInteraction.cardId,
          slotManaged: userInteraction.slotManaged,
          startEditingTimeStamp: userInteraction.startEditingTimeStamp,
          applyChangesTimeStamp: userInteraction.applyChangesTimeStamp,
          events: userInteraction.events,
          earliestTimeFormat: isTwelveHours ? '12h' : '24h',
          latestTimeFormat: isTwelveHours ? '12h' : '24h'
        })
      })
    }
  })
}
